// D:\aifrai\FireBoiler\src\App.jsx

import './polyfills.js';
import React, { Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// Styles
import './styles/styles.css';

// Core Components & Contexts
import LoadingScreen from './components/common/LoadingScreen.jsx';
import ErrorBoundary from './components/common/ErrorBoundary.jsx';
import FirebaseInitializer from './router/FirebaseInitializer.jsx';
import Header from './components/layout/Header.jsx';
import Footer from './components/layout/Footer.jsx';
import { UserProvider } from './contexts/UserContext.jsx';
import { GuestProvider } from './contexts/GuestContext.jsx';
import { LoadingProvider } from './contexts/LoadingContext.jsx';
import { StripePlansProvider } from './contexts/StripePlansContext.jsx';
import StableStripeProvider from './components/stripe/StableStripeProvider.jsx';
import { ProtectedRoute } from './router/ProtectedRoute.jsx';

// --- Lazy Load ALL Route Components ---
const LandingPage = lazy(() => import('./pages/LandingPage.jsx'));
const LoginPage = lazy(() => import('./pages/LoginPage.jsx'));
const OAuthCallback = lazy(() => import('./components/OAuthCallback.jsx'));
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'));
const PaymentPage = lazy(() => import('./pages/PaymentPage.jsx'));
const PricingPage = lazy(() => import('./pages/PricingPage.jsx'));
const AboutPage = lazy(() => import('./pages/AboutPage.jsx'));
const ContactPage = lazy(() => import('./pages/ContactPage.jsx'));
const FAQPage = lazy(() => import('./pages/FAQPage.jsx'));
const ParticipationPage = lazy(() => import('./pages/ParticipationPage.jsx'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage.jsx'));
const AnalysisAreasPage = lazy(() => import('./pages/AnalysisAreasPage.jsx'));
const BudgetAnalysisPage = lazy(() => import('./pages/BudgetAnalysisPage.jsx'));
const TrainingsPage = lazy(() => import('./pages/TrainingsPage.jsx'));
const ResearchPage = lazy(() => import('./pages/ResearchPage.jsx'));
const TermsPage = lazy(() => import('./pages/TermsPage.jsx'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage.jsx'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage.jsx'));
const ProfilePage = lazy(() => import('./pages/ProfilePage.jsx'));
const ReferralsPage = lazy(() => import('./pages/ReferralsPage.jsx'));

// --- NEW PUBLIC DATA PAGES ---
const SubsidiesDashboardPage = lazy(() => import('./pages/SubsidiesDashboardPage.jsx'));
const SubsidiesExplorerPage = lazy(() => import('./pages/SubsidiesExplorerPage.jsx'));
// --- PATCH START ---
// Lazily import the new ActorDetailPage component
const ActorDetailPage = lazy(() => import('./pages/ActorDetailPage.jsx'));
// --- PATCH END ---


// --- AppContent: Manages layout visibility ---
const AppContent = () => {
  const location = useLocation();
  const noLayoutRoutes = ['/login', '/auth/callback'];

  const showHeader = !noLayoutRoutes.some(route => location.pathname.startsWith(route));
  const showFooter = !noLayoutRoutes.some(route => location.pathname.startsWith(route));

  return (
    <div className="app-container flex flex-col min-h-screen">
      {showHeader && <Header />}
      <main id="main-content" role="main" className="flex-grow">
        <Suspense fallback={<LoadingScreen type="page" />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<OAuthCallback />} />
            
            {/* --- CORE PUBLIC DATA EXPLORER ROUTES --- */}
            <Route path="/subventions" element={<SubsidiesDashboardPage />} />
            <Route path="/subventions/explorer" element={<SubsidiesExplorerPage />} />
            {/* --- PATCH START --- */}
            {/* Add the new, specific route for the actor detail page */}
            <Route path="/acteurs/:siren" element={<ActorDetailPage />} />
            {/* Remove the old, generic route to avoid conflicts */}
            {/* <Route path="/subventions/:category/:id" element={<SubsidiesExplorerPage />} /> */}
            {/* --- PATCH END --- */}
            
            {/* Public Informational Pages */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/participation" element={<ParticipationPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/analysis-areas" element={<AnalysisAreasPage />} />
            <Route path="/budget-analysis" element={<BudgetAnalysisPage />} />
            <Route path="/trainings" element={<TrainingsPage />} />
            <Route path="/research" element={<ResearchPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            
            {/* Protected Routes (User Account Area) */}
            <Route path="/dashboard/*" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/payment" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/referrals" element={<ProtectedRoute><ReferralsPage /></ProtectedRoute>} />

            {/* 404 Catch-all */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      {showFooter && <Footer />}
    </div>
  );
};

// --- Root App Component: Provider Hierarchy ---
const App = () => (
  <Router>
    <ErrorBoundary>
      <LoadingProvider>
        <FirebaseInitializer>
          <GuestProvider>
            <UserProvider>
              <StableStripeProvider>
                <StripePlansProvider>
                  <AppContent />
                </StripePlansProvider>
              </StableStripeProvider>
            </UserProvider>
          </GuestProvider>
        </FirebaseInitializer>
      </LoadingProvider>
    </ErrorBoundary>
  </Router>
);

// --- Render Logic ---
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Fatal Error: Root element with id 'root' not found in the DOM.");
}

const root = createRoot(rootElement);
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);

export default App;